import { useWorkflow } from '@cobliteam/ai';
import type { Permission } from '@cobliteam/permissions';
import { useCallback, useEffect } from 'react';
import { create } from 'zustand';
import useUser from '../useEntity/useUser';

export const RECOMMENDATION_WORKFLOW_ID = 'ai-recommendations';

export interface AiRecommendationAction {
  id: string;
  name: string;
  description: string;
}

export interface AiRecommendation {
  journeyId: string;
  journeyName: string;
  reasons: string[];
  description: string;
  shortDescription: string;
  aiPrompt: string;
  criticality: string;
  actions: AiRecommendationAction[];
  estimatedMinutes: number;
}

export interface AiRecommendationsProgress {
  completedJourneys: number;
  totalJourneys: number;
  score: number;
}

interface AiRecommendationsState {
  recommendations: AiRecommendation[];
  improvementRecommendations: AiRecommendation[];
  progress: AiRecommendationsProgress;
  isLoading: boolean;
  isError: boolean;
  initializedForFleetId: string | null;
  lastFetchedAt: number | null;
  floatingCardDismissed: boolean;
}

const initialState: AiRecommendationsState = {
  recommendations: [],
  improvementRecommendations: [],
  progress: { completedJourneys: 0, totalJourneys: 0, score: 0 },
  isLoading: false,
  isError: false,
  initializedForFleetId: null,
  lastFetchedAt: null,
  floatingCardDismissed: typeof window !== 'undefined' && sessionStorage.getItem('ai-rec-floating-dismissed') === 'true',
};

const STALE_TIME_MS = 10 * 60 * 1000; // 10 minutes

export const criticalityConfig: Record<string, { label: string }> = {
  critical: { label: 'Alta prioridade' },
  urgent: { label: 'Média prioridade' },
  important: { label: 'Baixa prioridade' },
  suggestive: { label: 'Baixa prioridade' },
};

export const permissionsConfig: Record<string, Permission> = {
  'driver-registration': 'create-update-delete:driver',
  'user-profile': 'create-update-delete:users-and-invites',
  installation: 'create-update-delete:crm',
  'vehicle-groups': 'create-update-delete:vehicle',
  'fleet-policies': 'config-and-view:custom-alerts',
  checklists: 'full-control:checklist',
  geofences: 'create-update-delete:places-of-interest',
  'sensibility-configuration': 'request:camera-media',
  'cam-audio': 'request:camera-media',
  'wifi-configuration': 'request:camera-media',
};

const useAiRecommendationsStoreBase = create<AiRecommendationsState>(() => ({ ...initialState }));

const dismissFloatingCard = () => {
  useAiRecommendationsStoreBase.setState({ floatingCardDismissed: true });
  try {
    sessionStorage.setItem('ai-rec-floating-dismissed', 'true');
  } catch {
    // sessionStorage unavailable
  }
};

const useAiRecommendationsStore = () => {
  const { getSession } = useUser();
  const fleetId = getSession()?.fleet?.id;

  const { createRun, startAsync } = useWorkflow({ workflowId: RECOMMENDATION_WORKFLOW_ID });

  const fetch = useCallback(async () => {
    if (!fleetId) return;

    const { initializedForFleetId, isLoading, lastFetchedAt } = useAiRecommendationsStoreBase.getState();

    if (initializedForFleetId && initializedForFleetId !== fleetId) {
      useAiRecommendationsStoreBase.setState({ ...initialState });
    } else if (isLoading) {
      return;
    } else if (initializedForFleetId === fleetId && lastFetchedAt && Date.now() - lastFetchedAt < STALE_TIME_MS) {
      return;
    }

    useAiRecommendationsStoreBase.setState({ isLoading: true, isError: false });

    try {
      await createRun();
      const result = await startAsync({ fleetId });

      const data = (result as any)?.result;
      useAiRecommendationsStoreBase.setState({
        recommendations: data?.recommendations ?? [],
        improvementRecommendations: data?.improvementRecommendations ?? [],
        progress: {
          completedJourneys: data?.completedJourneys ?? 0,
          totalJourneys: data?.totalJourneys ?? 0,
          score: data?.score ?? 0,
        },
        isLoading: false,
        isError: false,
        initializedForFleetId: fleetId,
        lastFetchedAt: Date.now(),
      });
    } catch {
      useAiRecommendationsStoreBase.setState({ isLoading: false, isError: true, initializedForFleetId: fleetId });
    }
  }, [fleetId, createRun, startAsync]);

  const refetch = useCallback(async () => {
    if (!fleetId) return;

    const { isLoading } = useAiRecommendationsStoreBase.getState();
    if (isLoading) return;

    useAiRecommendationsStoreBase.setState({ isLoading: true, isError: false });

    try {
      await createRun();
      const result = await startAsync({ fleetId });

      const data = (result as any)?.result;
      useAiRecommendationsStoreBase.setState({
        recommendations: data?.recommendations ?? [],
        improvementRecommendations: data?.improvementRecommendations ?? [],
        progress: data?.progress ?? { completedJourneys: 0, totalJourneys: 0 },
        isLoading: false,
        isError: false,
        initializedForFleetId: fleetId ?? null,
        lastFetchedAt: Date.now(),
      });
    } catch {
      useAiRecommendationsStoreBase.setState({ isLoading: false, isError: true, initializedForFleetId: fleetId ?? null });
    }
  }, [fleetId, createRun, startAsync]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const { recommendations, improvementRecommendations, progress, isLoading, isError, floatingCardDismissed } = useAiRecommendationsStoreBase();

  return { recommendations, improvementRecommendations, progress, isLoading, isError, refetch, floatingCardDismissed, dismissFloatingCard };
};

export default useAiRecommendationsStore;
